import React, { useState, useCallback } from 'react';
import { View, StyleSheet, ScrollView, Alert, RefreshControl } from 'react-native';
import { Text, Card, Button, TextInput, Portal, Dialog, ActivityIndicator, Chip, IconButton, Divider, Switch, Menu } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const SucursalesScreen = () => {
  const navigation = useNavigation();
  const { activeSucursal, switchSucursal } = useAuth();
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ nombre: '', telefono: '', direccion: '', rtn: '' });
  // Copiar catálogo al crear
  const [copiarCatalogo, setCopiarCatalogo] = useState(false);
  const [copiarDe, setCopiarDe] = useState(null);
  const [showCopiarMenu, setShowCopiarMenu] = useState(false);
  const [copiarClientes, setCopiarClientes] = useState(false);
  const [codigoBuscar, setCodigoBuscar] = useState('');
  const [codigoReemplazar, setCodigoReemplazar] = useState('');

  const cargar = useCallback(async () => {
    try {
      const data = await api.getSucursales();
      setSucursales(Array.isArray(data) ? data : []);
    } catch (e) {
      Alert.alert('Error', e.message || 'No se pudieron cargar las sucursales');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(useCallback(() => { cargar(); }, [cargar]));

  const activeId = activeSucursal?.id || null;
  // Determina si una fila está activa: la matriz está activa cuando no hay
  // sucursal activa seleccionada.
  const esFilaActiva = (s) => (activeId ? s.id === activeId : !!s.es_matriz);

  const handleSwitch = async (s) => {
    await switchSucursal(s);
    Alert.alert(
      'Sucursal activa',
      `Ahora estás trabajando en "${s.nombre}". Los datos (inventario, ventas, etc.) se mostrarán de esta sucursal.`
    );
  };

  const matriz = sucursales.find((s) => s.es_matriz) || sucursales[0] || null;
  const copiarDeSuc = sucursales.find((s) => s.id === copiarDe) || matriz;

  const abrirCrear = () => {
    setForm({ nombre: '', telefono: '', direccion: '', rtn: '' });
    setCopiarCatalogo(false);
    setCopiarDe(matriz ? matriz.id : null);
    setCopiarClientes(false);
    setCodigoBuscar('');
    setCodigoReemplazar('');
    setShowDialog(true);
  };

  const guardar = async () => {
    if (!form.nombre.trim()) {
      Alert.alert('Falta el nombre', 'Ingresa el nombre de la sucursal');
      return;
    }
    setSaving(true);
    try {
      const res = await api.createSucursal({
        nombre: form.nombre.trim(),
        telefono: form.telefono.trim() || null,
        direccion: form.direccion.trim() || null,
        rtn: form.rtn.trim() || null,
        copiar_de: copiarCatalogo && copiarDe ? copiarDe : null,
        copiar_clientes: copiarCatalogo && copiarClientes,
        codigo_buscar: copiarCatalogo ? codigoBuscar.trim() : '',
        codigo_reemplazar: copiarCatalogo ? codigoReemplazar.trim() : '',
      });
      setShowDialog(false);
      await cargar();
      const copiados = res?.productos_copiados || 0;
      const cli = res?.clientes_copiados || 0;
      Alert.alert(
        'Listo',
        copiados > 0
          ? `Sucursal creada con ${copiados} productos${cli > 0 ? ` y ${cli} clientes` : ''} copiados (precios incluidos, stock en 0).`
          : 'Sucursal creada. Arranca vacía: entra a la sucursal y carga su inventario.'
      );
    } catch (e) {
      Alert.alert('Error', e.message || 'No se pudo crear la sucursal');
    } finally {
      setSaving(false);
    }
  };

  const cambiarEstado = (s) => {
    const nuevo = s.estado === 'activo' ? 'suspendido' : 'activo';
    Alert.alert(
      nuevo === 'activo' ? 'Activar sucursal' : 'Suspender sucursal',
      `¿${nuevo === 'activo' ? 'Activar' : 'Suspender'} "${s.nombre}"?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Confirmar',
          onPress: async () => {
            try {
              await api.setSucursalEstado(s.id, nuevo);
              await cargar();
            } catch (e) {
              Alert.alert('Error', e.message || 'No se pudo cambiar el estado');
            }
          },
        },
      ]
    );
  };

  const eliminar = (s) => {
    Alert.alert(
      'Eliminar sucursal',
      `¿Eliminar "${s.nombre}"? Se borrará su catálogo y configuración. Sus usuarios pasarán a la sucursal principal. Esto no se puede deshacer.`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Eliminar',
          style: 'destructive',
          onPress: async () => {
            try {
              if (activeSucursal && activeSucursal.id === s.id) await switchSucursal(null);
              await api.deleteSucursal(s.id);
              await cargar();
              Alert.alert('Listo', 'Sucursal eliminada.');
            } catch (e) {
              Alert.alert('No se pudo eliminar', e.message || 'Error al eliminar la sucursal');
            }
          },
        },
      ]
    );
  };

  // Activa la sucursal y lleva a Ajustes para editar su info de recibos/documentos.
  const configurarRecibos = async (s) => {
    await switchSucursal(s);
    navigation.navigate('Ajustes');
  };

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#007bff" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.scroll}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); cargar(); }} />}
      >
        <Text style={styles.hint}>
          Cada sucursal maneja su propio inventario, usuarios, ventas, precios e
          info de recibos. Toca “Trabajar aquí” para operar en ella, o “Info de
          recibos” para editar los datos que salen en sus facturas y tickets.
        </Text>

        {sucursales.map((s) => {
          const activa = esFilaActiva(s);
          return (
            <Card key={s.id} style={[styles.card, activa && styles.cardActiva]}>
              <Card.Content>
                <View style={styles.row}>
                  <View style={{ flex: 1 }}>
                    <View style={styles.nombreRow}>
                      <Ionicons name="business" size={18} color={activa ? '#007bff' : '#555'} />
                      <Text style={styles.nombre}>{s.nombre}</Text>
                      {s.es_matriz ? <Chip compact style={styles.matrizChip} textStyle={styles.matrizChipText}>Matriz</Chip> : null}
                    </View>
                    <Text style={styles.meta}>
                      {s.estado === 'activo' ? '● Activa' : '○ Suspendida'}
                      {activa ? '  ·  Trabajando aquí' : ''}
                    </Text>
                  </View>
                  {!s.es_matriz && (
                    <>
                      <IconButton
                        icon={s.estado === 'activo' ? 'pause-circle-outline' : 'play-circle-outline'}
                        iconColor={s.estado === 'activo' ? '#e67e22' : '#27ae60'}
                        size={24}
                        onPress={() => cambiarEstado(s)}
                      />
                      <IconButton
                        icon="trash-can-outline"
                        iconColor="#c62828"
                        size={22}
                        onPress={() => eliminar(s)}
                      />
                    </>
                  )}
                </View>
                <Divider style={styles.div} />
                <View style={styles.accionesRow}>
                  <Button
                    mode={activa ? 'contained' : 'outlined'}
                    onPress={() => handleSwitch(s)}
                    disabled={activa || s.estado !== 'activo'}
                    compact
                    style={styles.accionBtn}
                  >
                    {activa ? 'Sucursal activa' : 'Trabajar aquí'}
                  </Button>
                  <Button
                    mode="text"
                    icon="receipt"
                    onPress={() => configurarRecibos(s)}
                    disabled={s.estado !== 'activo'}
                    compact
                  >
                    Info de recibos
                  </Button>
                </View>
              </Card.Content>
            </Card>
          );
        })}

        <Button
          mode="contained"
          icon="plus"
          onPress={abrirCrear}
          style={styles.addBtn}
        >
          Nueva sucursal
        </Button>
      </ScrollView>

      <Portal>
        <Dialog visible={showDialog} onDismiss={() => setShowDialog(false)}>
          <Dialog.Title>Nueva sucursal</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label="Nombre *"
              value={form.nombre}
              onChangeText={(v) => setForm((p) => ({ ...p, nombre: v }))}
              style={styles.input}
              mode="outlined"
            />
            <TextInput
              label="Teléfono"
              value={form.telefono}
              onChangeText={(v) => setForm((p) => ({ ...p, telefono: v }))}
              style={styles.input}
              mode="outlined"
              keyboardType="phone-pad"
            />
            <TextInput
              label="Dirección"
              value={form.direccion}
              onChangeText={(v) => setForm((p) => ({ ...p, direccion: v }))}
              style={styles.input}
              mode="outlined"
            />
            <TextInput
              label="RTN"
              value={form.rtn}
              onChangeText={(v) => setForm((p) => ({ ...p, rtn: v }))}
              style={styles.input}
              mode="outlined"
            />

            <View style={styles.switchRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.switchLabel}>Copiar catálogo</Text>
                <Text style={styles.switchSub}>Productos y precios de otra sucursal (stock en 0)</Text>
              </View>
              <Switch value={copiarCatalogo} onValueChange={setCopiarCatalogo} />
            </View>

            {copiarCatalogo && sucursales.length > 0 && (
              <Menu
                visible={showCopiarMenu}
                onDismiss={() => setShowCopiarMenu(false)}
                anchor={
                  <Button mode="outlined" onPress={() => setShowCopiarMenu(true)} style={styles.copiarBtn} icon="content-copy">
                    Desde: {copiarDeSuc ? copiarDeSuc.nombre : 'Seleccionar'}
                  </Button>
                }
              >
                {sucursales.map((s) => (
                  <Menu.Item
                    key={s.id}
                    title={s.nombre + (s.es_matriz ? ' (Matriz)' : '')}
                    onPress={() => { setCopiarDe(s.id); setShowCopiarMenu(false); }}
                  />
                ))}
              </Menu>
            )}

            {copiarCatalogo && (
              <>
                {/* Cambiar prefijo de código (opcional) */}
                <Text style={styles.codigoLabel}>Cambiar código (opcional)</Text>
                <View style={styles.codigoRow}>
                  <TextInput
                    label="De"
                    value={codigoBuscar}
                    onChangeText={setCodigoBuscar}
                    style={styles.codigoInput}
                    mode="outlined"
                    dense
                    autoCapitalize="characters"
                    placeholder="MLX"
                  />
                  <Text style={styles.codigoFlecha}>→</Text>
                  <TextInput
                    label="A"
                    value={codigoReemplazar}
                    onChangeText={setCodigoReemplazar}
                    style={styles.codigoInput}
                    mode="outlined"
                    dense
                    autoCapitalize="characters"
                    placeholder="MTL"
                  />
                </View>
                {codigoBuscar.trim() !== '' && (
                  <Text style={styles.codigoHint}>
                    Ej: {codigoBuscar.trim()}184 → {(codigoReemplazar.trim() || '') + '184'}
                  </Text>
                )}

                {/* Copiar clientes */}
                <View style={styles.switchRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.switchLabel}>Copiar clientes</Text>
                    <Text style={styles.switchSub}>Todos los clientes de la sucursal origen</Text>
                  </View>
                  <Switch value={copiarClientes} onValueChange={setCopiarClientes} />
                </View>
              </>
            )}

            <Text style={styles.dialogHint}>Sin copiar catálogo, la sucursal arranca vacía.</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowDialog(false)} disabled={saving}>Cancelar</Button>
            <Button onPress={guardar} loading={saving} disabled={saving}>Crear</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  scroll: { padding: 16, paddingBottom: 40 },
  hint: { fontSize: 13, color: '#666', marginBottom: 12, lineHeight: 18 },
  card: { marginBottom: 12, backgroundColor: '#fff' },
  cardActiva: { borderColor: '#007bff', borderWidth: 2 },
  row: { flexDirection: 'row', alignItems: 'center' },
  nombreRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' },
  nombre: { fontSize: 16, fontWeight: 'bold', color: '#222', marginLeft: 6 },
  matrizChip: { marginLeft: 8, backgroundColor: '#e3f2fd', height: 24 },
  matrizChipText: { fontSize: 11, color: '#007bff' },
  meta: { fontSize: 12, color: '#777', marginTop: 4, marginLeft: 24 },
  div: { marginVertical: 10 },
  accionesRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' },
  accionBtn: { marginRight: 8 },
  addBtn: { marginTop: 8, backgroundColor: '#007bff' },
  input: { marginBottom: 10, backgroundColor: '#fff' },
  dialogHint: { fontSize: 12, color: '#888', marginTop: 8 },
  switchRow: { flexDirection: 'row', alignItems: 'center', marginTop: 4, marginBottom: 6 },
  switchLabel: { fontSize: 14, color: '#222', fontWeight: '600' },
  switchSub: { fontSize: 11, color: '#888' },
  copiarBtn: { marginTop: 4, marginBottom: 4 },
  codigoLabel: { fontSize: 13, color: '#444', fontWeight: '600', marginTop: 10 },
  codigoRow: { flexDirection: 'row', alignItems: 'center', marginTop: 4 },
  codigoInput: { flex: 1, backgroundColor: '#fff' },
  codigoFlecha: { marginHorizontal: 8, fontSize: 18, color: '#666' },
  codigoHint: { fontSize: 11, color: '#1565c0', marginTop: 4 },
});

export default SucursalesScreen;
